import { useState } from "react";
import {
  Box, Typography, IconButton, Popover, TextField, useTheme,
} from "@mui/material";
import ChevronLeftIcon  from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function DateCalendarPicker({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  size = "small",
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";

  const [anchorEl, setAnchorEl] = useState(null);
  const [viewMonth, setViewMonth] = useState(moment(value || undefined));

  const open = Boolean(anchorEl);
  const selected = value ? moment(value, "YYYY-MM-DD", true) : null;

  const startOfMonth = viewMonth.clone().startOf("month");
  const startOffset = startOfMonth.day();
  const daysInMonth = viewMonth.daysInMonth();

  const isDisabled = (day) => {
    const date = viewMonth.clone().date(day).format("YYYY-MM-DD");
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const handleSelect = (day) => {
    if (isDisabled(day)) return;
    onChange(viewMonth.clone().date(day).format("YYYY-MM-DD"));
    setAnchorEl(null);
  };

  const inputSx = {
    "& .MuiInputBase-root": {
      fontSize: "13px",
      height: size === "small" ? "36px" : "40px",
      backgroundColor: cardBg,
      borderRadius: "8px",
      cursor: "pointer",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  return (
    <>
      <TextField
        label={label}
        size={size}
        value={selected?.isValid() ? selected.format("MMM D, YYYY") : ""}
        placeholder="Pick a date"
        onClick={(e) => {
          setViewMonth(moment(value || undefined));
          setAnchorEl(e.currentTarget);
        }}
        InputProps={{
          readOnly: true,
          endAdornment: <CalendarTodayIcon sx={{ fontSize: "16px", color: textSec, mr: "4px" }} />,
        }}
        sx={{ ...inputSx, minWidth: "160px" }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: "6px",
            p: "12px",
            borderRadius: "10px",
            border: `1px solid ${border}`,
            backgroundColor: cardBg,
            boxShadow: isDark
              ? "0 8px 24px rgba(0,0,0,0.4)"
              : "0 8px 24px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box sx={{ width: "260px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "10px" }}>
            <IconButton size="small" onClick={() => setViewMonth((m) => m.clone().subtract(1, "month"))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: textPri }}>
              {viewMonth.format("MMMM YYYY")}
            </Typography>
            <IconButton size="small" onClick={() => setViewMonth((m) => m.clone().add(1, "month"))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", mb: "4px" }}>
            {WEEKDAYS.map((d) => (
              <Typography
                key={d}
                sx={{
                  fontSize: "10px", fontWeight: 600, textAlign: "center",
                  color: textSec, py: "4px",
                }}
              >
                {d}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
            {Array.from({ length: startOffset }).map((_, i) => (
              <Box key={`empty-${i}`} sx={{ height: "32px" }} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = viewMonth.clone().date(day).format("YYYY-MM-DD");
              const isSelected = selected?.isValid() && selected.format("YYYY-MM-DD") === dateStr;
              const isToday = moment().format("YYYY-MM-DD") === dateStr;
              const disabled = isDisabled(day);

              return (
                <Box
                  key={day}
                  onClick={() => handleSelect(day)}
                  sx={{
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: isSelected || isToday ? 600 : 400,
                    cursor: disabled ? "not-allowed" : "pointer",
                    color: disabled ? textSec : isSelected ? "#fff" : textPri,
                    opacity: disabled ? 0.35 : 1,
                    backgroundColor: isSelected
                      ? accent
                      : isToday
                      ? (isDark ? "rgba(29,158,117,0.15)" : "rgba(29,158,117,0.1)")
                      : "transparent",
                    "&:hover": disabled ? {} : {
                      backgroundColor: isSelected ? accent : (isDark ? "rgba(29,158,117,0.12)" : "rgba(29,158,117,0.08)"),
                    },
                  }}
                >
                  {day}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
